import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProjects } from '../context/ProjectsContext';
import ImageCarousel from '../components/ImageCarousel';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiExternalLink, FiMapPin } = FiIcons;

const ProjectDetail = () => {
  const { id } = useParams();
  const { getProjectById } = useProjects();
  const project = getProjectById(id);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center text-black">
          <h2 className="font-poppins text-2xl font-bold mb-4">
            Project not found
          </h2>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Collect all images for the carousel (columns A-Z only)
  const images = [];
  for (let i = 1; i <= 26; i++) {
    const imageKey = `Project Image ${i}`;
    if (project[imageKey] && project[imageKey].trim() !== '') {
      images.push(project[imageKey]);
    }
  }

  // Collect all video links
  const videos = [];
  Object.keys(project).forEach(key => {
    if (key.toLowerCase().includes('video') && project[key] && project[key].trim() !== '') {
      videos.push(project[key]);
    }
  });

  // Special handling for Exotic & Classic Car Club Orlando video
  if (project['Project Name'] === 'Exotic & Classic Car Club Orlando' || project['Project Name'] === 'Exotic Car Club Orlando') {
    videos.push('https://vids.daszeph.com/file/RB/Architectural-Projects/ECCCO-ANIMATION-16-9-FULL-VIDEO.html?player=DASZeph');
  }

  // Get all data fields to display (excluding image fields, video fields, and id)
  const excludeFields = ['id'];
  
  // Add all Project Image fields to exclude list
  for (let i = 1; i <= 26; i++) {
    excludeFields.push(`Project Image ${i}`);
  }
  
  // Add video fields to exclude list
  Object.keys(project).forEach(key => {
    if (key.toLowerCase().includes('video')) {
      excludeFields.push(key);
    }
  });

  const dataFields = Object.keys(project).filter(key => 
    !excludeFields.includes(key) && 
    project[key] && 
    project[key].toString().trim() !== ''
  );

  const formatGoogleMapsUrl = (location) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
  };

  // Check if URL is an iframe-based content URL (for videos)
  const isIframeUrl = (url) => {
    return url.includes('content.raheem.work') && url.includes('.html') ||
           url.includes('vids.daszeph.com') && url.includes('.html');
  };

  const isVideoUrl = (url) => {
    return url.includes('youtube.com') || 
           url.includes('youtu.be') || 
           url.includes('vimeo.com') || 
           url.includes('.mp4') || 
           url.includes('.mov') || 
           url.includes('.avi') ||
           isIframeUrl(url); // Include iframe URLs as potential video content
  };

  const getEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url; // Return original URL for iframe content
  };

  // Function to format Project Outcomes & Recognition text
  const formatProjectOutcomes = (text) => {
    if (!text) return text;
    
    return text
      .replace(/Achievement\(s\):/g, '<br/><strong>Achievement(s):</strong>')
      .replace(/Key Challenge\(s\):/g, '<br/><strong>Key Challenge(s):</strong>')
      .replace(/Lesson\(s\) Learnt\/ Career Growth:/g, '<br/><strong>Lesson(s) Learnt/ Career Growth:</strong>');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white text-black"
    >
      {/* Header */}
      <div className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors mb-6"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Projects
          </Link>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-blanka text-4xl lg:text-5xl font-bold text-accent-primary"
          >
            {project['Project Name']}
          </motion.h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {images.length > 0 && <ImageCarousel images={images} />}
            
            {/* Videos Section - Moved here under images */}
            {videos.length > 0 && (
              <div>
                <h2 className="font-barlow text-2xl font-semibold text-accent-primary mb-6">
                  Additional Content and Renderings
                </h2>
                <div className="space-y-6">
                  {videos.map((video, index) => (
                    <div key={index} className="w-full">
                      {isIframeUrl(video) ? (
                        <iframe
                          src={getEmbedUrl(video)}
                          title={`Project video ${index + 1}`}
                          className="w-full border-2 border-accent-primary rounded-lg"
                          style={{ 
                            aspectRatio: '16/9',
                            overflow: 'hidden'
                          }}
                          allowFullScreen
                        />
                      ) : isVideoUrl(video) ? (
                        <iframe
                          src={getEmbedUrl(video)}
                          title={`Project video ${index + 1}`}
                          className="w-full border-2 border-accent-primary rounded-lg"
                          style={{ 
                            aspectRatio: '16/9',
                            overflow: 'hidden'
                          }}
                          allowFullScreen
                        />
                      ) : (
                        <video
                          src={video}
                          controls
                          className="w-full border-2 border-accent-primary rounded-lg bg-black"
                          style={{ 
                            aspectRatio: '16/9'
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Project Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="space-y-8"
          >
            {dataFields.map((field, index) => (
              <motion.div
                key={field}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
              >
                <h3 className="font-barlow text-lg font-semibold text-accent-primary mb-2">
                  {field}
                </h3>
                {field === 'Location' ? (
                  <a
                    href={formatGoogleMapsUrl(project[field])}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-barlow text-black underline hover:text-accent-primary transition-colors inline-flex items-center gap-2"
                  >
                    <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                    {project[field]}
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                ) : field === 'Project Outcomes & Recognition' ? (
                  <div 
                    className="font-barlow text-black leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: formatProjectOutcomes(project[field]) 
                    }}
                  />
                ) : (
                  <p className="font-barlow text-black leading-relaxed">
                    {project[field]}
                  </p>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-20 pt-8 border-t border-gray-300"
        >
          <p className="font-poppins text-sm text-gray-600 text-center">
            All information presented on this page is accurate to the best of my knowledge, 
            with appropriate permissions obtained for showcasing this project. Proper credit 
            is given to all contributors involved.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProjectDetail;